// Opportunity strategy (apps/server/src/strategy.mjs): an opportunity's
// strategy and pre-mortem, the playbook review, and accepting or dismissing a
// proposed guideline.
import { authenticatedFetch } from '../auth/authClient';
import type { StrategyRun, StrategyState } from './strategyRuns';

async function json<T>(res: Response, fallback: string): Promise<T> {
  const data = (await res.json().catch(() => null)) as (T & { error?: string }) | null;
  if (!res.ok || data === null) throw new Error(data?.error ?? `${fallback} (${res.status}).`);
  return data;
}

const strategyPath = (opportunityId: string) => `/api/opportunities/${encodeURIComponent(opportunityId)}/strategy`;
const post = (body: unknown) => ({ method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) });

export async function fetchStrategy(opportunityId: string, init?: { signal?: AbortSignal }): Promise<StrategyState> {
  return json(await authenticatedFetch(strategyPath(opportunityId), init), 'Could not load the strategy');
}

/** Runs a strategy or pre-mortem now; the request waits for the model. */
export async function runStrategy(opportunityId: string, kind: 'strategy' | 'pre_mortem'): Promise<{ configured: boolean; run: StrategyRun }> {
  return json(await authenticatedFetch(strategyPath(opportunityId), post({ kind })), 'Could not run it');
}

/** Runs what is queued for this opportunity (the pre-mortem written at Propose). */
export async function processStrategy(opportunityId: string): Promise<StrategyState> {
  return json(await authenticatedFetch(`${strategyPath(opportunityId)}/process`, post({})), 'Could not run it');
}

export async function runPlaybookReview(): Promise<{ configured: boolean; run: StrategyRun }> {
  return json(await authenticatedFetch('/api/playbook/runs', post({})), 'Could not review the playbook');
}

export async function reviewGuideline(guidelineId: string, decision: 'accept' | 'dismiss'): Promise<{ active: boolean; review_status: string }> {
  return json(await authenticatedFetch(`/api/guidelines/${encodeURIComponent(guidelineId)}/review`, post({ decision })), 'Could not save that');
}
